import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Ambulance, Scale, UserRound } from 'lucide-react';
import { post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { humanize } from '@/lib/utils';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Checkbox, Field, Input, Textarea } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useToast } from '@/components/ui/toast';
import { PatientPicker } from '../patients/PatientPicker';
import { safeReturnTo } from '../opd/BookAppointmentPage';

const EMPTY = {
  patientId: '',
  unknownPatientLabel: '',
  arrivalMode: 'walk_in',
  chiefComplaint: '',
  isMlc: false,
  mlcDetails: '',
  broughtByName: '',
  broughtByPhone: '',
};

export function ErRegisterPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const returnTo = safeReturnTo(params.get('returnTo'), '/er');
  const [unknown, setUnknown] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const register = useMutation({
    mutationFn: () =>
      post('/er/cases', {
        patientId: unknown ? null : form.patientId,
        unknownPatientLabel: unknown ? form.unknownPatientLabel : undefined,
        arrivalMode: form.arrivalMode,
        chiefComplaint: form.chiefComplaint || undefined,
        isMlc: form.isMlc,
        mlcDetails: form.isMlc ? form.mlcDetails : undefined,
        broughtByName: form.broughtByName || undefined,
        broughtByPhone: form.broughtByPhone || undefined,
      }),
    onSuccess: () => {
      toast.success('Arrival registered', 'Triage them next');
      void queryClient.invalidateQueries({ queryKey: ['er-board'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const ready = unknown ? form.unknownPatientLabel.trim().length >= 2 : Boolean(form.patientId);
  const dirty = !register.isSuccess && (unknown || JSON.stringify(form) !== JSON.stringify(EMPTY));

  return (
    <FormScreen
      title="Register an arrival"
      description="Registration must never delay care — an unidentified patient can be registered as unknown"
      icon={Ambulance}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        setError(null);
        register.mutate();
      }}
    >
      <FormSection title={tr('opd', 'patient')} icon={UserRound} columns={1}>
        <Checkbox checked={unknown} onCheckedChange={setUnknown} label="Unidentified arrival — no patient record yet" />
        {unknown ? (
          <Field label="Temporary label" required hint="e.g. Unknown male, ~40, RTA">
            <Input value={form.unknownPatientLabel} onChange={(e) => setForm({ ...form, unknownPatientLabel: e.target.value })} />
          </Field>
        ) : (
          <Field label="Patient" required>
            <PatientPicker value={form.patientId} onChange={(patientId) => setForm({ ...form, patientId })} />
          </Field>
        )}
      </FormSection>

      <FormSection title={tr('opd', 'arrival')} icon={Ambulance}>
        <Field label="Arrival mode">
          <ChoiceButtons<string>
            layout="chips"
            value={form.arrivalMode}
            onChange={(value) => setForm({ ...form, arrivalMode: value })}
            options={['walk_in', 'ambulance', 'police', 'referral'].map((m) => ({ value: m, label: humanize(m) }))}
          />
        </Field>
        <Field label="Chief complaint">
          <Input value={form.chiefComplaint} onChange={(e) => setForm({ ...form, chiefComplaint: e.target.value })} />
        </Field>
        <Field label="Brought by">
          <Input value={form.broughtByName} onChange={(e) => setForm({ ...form, broughtByName: e.target.value })} />
        </Field>
        <Field label="Contact number">
          <Input value={form.broughtByPhone} onChange={(e) => setForm({ ...form, broughtByPhone: e.target.value })} />
        </Field>
      </FormSection>

      <FormSection title={tr('opd', 'medico_legal')} icon={Scale} columns={1}>
        <Checkbox checked={form.isMlc} onCheckedChange={(checked) => setForm({ ...form, isMlc: checked })} label="Medico-legal case" />
        {form.isMlc && (
          <Field label="MLC details" hint="Police station, FIR number, nature of the incident">
            <Textarea rows={2} value={form.mlcDetails} onChange={(e) => setForm({ ...form, mlcDetails: e.target.value })} />
          </Field>
        )}
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel="Register" loading={register.isPending} incomplete={!ready} cancelTo={returnTo} />
    </FormScreen>
  );
}
